// src/pages/MarketplaceIntegration/ProductMatching.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import useMarketplaceIntegration from './useMarketplaceIntegration';

const ProductMatching = ({ marketplaces }) => {
  const { userData } = useAuth();
  const { getMarketplaceProducts } = useMarketplaceIntegration();
  const [accountingProducts, setAccountingProducts] = useState([]);
  const [marketplaceProducts, setMarketplaceProducts] = useState({});
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [mappings, setMappings] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [marketplaceProductsLoading, setMarketplaceProductsLoading] = useState(false);

  useEffect(() => {
    if (!userData?.companyId) return;

    const fetchAccountingProducts = async () => {
      try {
        const productsRef = collection(db, 'companies', userData.companyId, 'products');
        const snapshot = await getDocs(productsRef);
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAccountingProducts(products);
      } catch (error) {
        console.error('Error fetching accounting products:', error);
      }
    };

    fetchAccountingProducts();
  }, [userData?.companyId]);

  useEffect(() => {
    // Initialize mappings
    const initialMappings = {};
    marketplaces.forEach(marketplace => {
      initialMappings[marketplace.name.toLowerCase()] = {};
    });
    setMappings(initialMappings);
  }, [marketplaces]);

  // Fetch marketplace products when a marketplace is selected
  useEffect(() => {
    const fetchMarketplaceProducts = async () => {
      if (!selectedMarketplace) {
        setMarketplaceProducts(prev => {
          const newProducts = { ...prev };
          marketplaces.forEach(marketplace => {
            newProducts[marketplace.name.toLowerCase()] = [];
          });
          return newProducts;
        });
        return;
      }

      setMarketplaceProductsLoading(true);
      try {
        // Find the marketplace ID by name
        const marketplace = marketplaces.find(m => m.name.toLowerCase() === selectedMarketplace);
        if (!marketplace) {
          throw new Error('Marketplace not found');
        }

        console.log('Fetching products for marketplace:', marketplace);

        // Fetch products from the selected marketplace
        const products = await getMarketplaceProducts(marketplace.id);
        
        console.log('Received products:', products);
        
        setMarketplaceProducts(prev => ({
          ...prev,
          [selectedMarketplace]: products
        }));
      } catch (error) {
        console.error('Error fetching marketplace products:', error);
      } finally {
        setMarketplaceProductsLoading(false);
      }
    };

    fetchMarketplaceProducts();
  }, [selectedMarketplace, marketplaces, getMarketplaceProducts]);

  const handleMarketplaceChange = (e) => {
    setSelectedMarketplace(e.target.value);
  };

  const handleMappingChange = (marketplaceSku, accountingProductId) => {
    setMappings(prev => ({
      ...prev,
      [selectedMarketplace]: {
        ...prev[selectedMarketplace],
        [marketplaceSku]: accountingProductId
      }
    }));
  };

  const handleSaveMappings = async () => {
    if (!selectedMarketplace) return;
    
    setLoading(true);
    
    try {
      // In a real implementation, this would save to Firestore
      // For now, we'll just simulate the save process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('Eşleştirmeler başarıyla kaydedildi!');
    } catch (error) {
      console.error('Error saving mappings:', error);
      alert('Eşleştirmeler kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const getAccountingProductById = (id) => {
    return accountingProducts.find(p => p.id === id);
  };

  const filteredMarketplaceProducts = selectedMarketplace 
    ? (marketplaceProducts[selectedMarketplace] || []).filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="product-matching">
      <div className="matching-header">
        <h2>Ürün Eşleştirme</h2>
        <p>Pazaryeri ürünleri ile muhasebe sisteminizdaki ürünleri eşleştirin</p>
      </div>

      <div className="matching-controls">
        <div className="form-group">
          <label htmlFor="marketplace-select">Pazaryeri Seçin</label>
          <select
            id="marketplace-select"
            value={selectedMarketplace}
            onChange={handleMarketplaceChange}
          >
            <option value="">Pazaryeri Seçin</option>
            {marketplaces.map(marketplace => (
              <option key={marketplace.id} value={marketplace.name.toLowerCase()}>
                {marketplace.name}
              </option>
            ))}
          </select>
        </div>

        {selectedMarketplace && (
          <div className="form-group">
            <label htmlFor="search">Ürün Ara</label>
            <input
              type="text"
              id="search"
              placeholder="Ürün adı veya SKU ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {marketplaceProductsLoading ? (
        <div className="loading-state">
          <p>Pazaryeri ürünleri yükleniyor...</p>
        </div>
      ) : selectedMarketplace ? (
        <div className="matching-table-container">
          <table className="matching-table">
            <thead>
              <tr>
                <th>Pazaryeri Ürünü</th>
                <th>SKU</th>
                <th>Fiyat</th>
                <th>Muhasebe Ürünü</th>
              </tr>
            </thead>
            <tbody>
              {filteredMarketplaceProducts.map(product => (
                <tr key={product.id}>
                  <td>
                    <div className="product-info">
                      <div className="product-name">{product.name}</div>
                    </div>
                  </td>
                  <td>{product.sku}</td>
                  <td>{product.price?.toFixed(2)} ₺</td>
                  <td>
                    <select
                      value={mappings[selectedMarketplace]?.[product.sku] || ''}
                      onChange={(e) => handleMappingChange(product.sku, e.target.value)}
                    >
                      <option value="">Eşleştirilmemiş</option>
                      {accountingProducts.map(accountingProduct => (
                        <option key={accountingProduct.id} value={accountingProduct.id}>
                          {accountingProduct.name} (Stok: {accountingProduct.quantity})
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="matching-actions">
            <button 
              className="save-button"
              onClick={handleSaveMappings}
              disabled={loading}
            >
              {loading ? 'Kaydediliyor...' : 'Eşleştirmeleri Kaydet'}
            </button>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <p>Başlamak için bir pazaryeri seçin</p>
        </div>
      )}

      {selectedMarketplace && !marketplaceProductsLoading && (
        <div className="mapping-summary">
          <h3>Eşleştirme Özeti</h3>
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">Toplam Pazaryeri Ürünü:</span>
              <span className="summary-value">{filteredMarketplaceProducts.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Eşleştirilmiş Ürün:</span>
              <span className="summary-value">
                {Object.values(mappings[selectedMarketplace] || {}).filter(Boolean).length}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Eşleştirilmemiş Ürün:</span>
              <span className="summary-value">
                {filteredMarketplaceProducts.length - Object.values(mappings[selectedMarketplace] || {}).filter(Boolean).length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductMatching;